import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Heading,
  HStack,
  Icon,
  Tag,
  Text,
  VStack,
} from '@chakra-ui/react';
import { mdiCalendar, mdiOpenInNew } from '@mdi/js';
import { format, parseISO } from 'date-fns';
import { getGoLiveDynamicText } from 'features/common/utils/goLiveText';
import {
  isNonProdEnvironment,
  validateURL,
} from '@sitecore-ui/portal-singular';
import {
  Media,
  MediaBody,
  MediaDescription,
  MediaFigure,
  MediaHover,
  MediaTitle,
} from 'features/common/components/MediaObject';
import { tenantsFallbackIcon } from 'features/common/utils/icons';
import { quickActionAdapter } from 'features/domains/portal/content/utils';
import { useDrawerStateDispatch, useMainState } from 'features/main/context';
import { useGetGoLiveDateQuery, Action } from 'gql/graphql';
import { useFeature } from 'lib/featureFlags/features';
import { History } from 'history';
import groupBy from 'lodash/groupBy';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { redirect } from '../../../../common/utils/utils';
import { closeDrawer } from '../../../../main/context/drawer/actionCreators';

export const onLinkClick = (
  url: string,
  history?: string[] | History<unknown>,
) => {
  redirect(url, history);
};

export const TenantsDrawer = () => {
  const state = useMainState();
  const [{ tenantDrawerVisible }, drawerDispatch] = useDrawerStateDispatch();

  const history = useHistory();
  const { app } = state;

  const product = useMemo(
    () =>
      app?.appearance?.web?.actions?.nodes?.find(
        (node: Action) => node.category === 'Direct Links',
      ),
    [app],
  );

  const onCloseDrawer = () => drawerDispatch(closeDrawer());

  const nodes = useMemo(
    () =>
      app?.appearance?.web?.actions?.nodes?.filter(
        (node: Action) => node.category !== 'Direct Links',
      ),
    [app],
  );

  const quickActions = useMemo(
    () =>
      nodes?.map((node) =>
        quickActionAdapter(node, { dispatch: drawerDispatch, history }),
      ) ?? [],
    [drawerDispatch, history, nodes],
  );

  const quickActionsGrouped = groupBy(quickActions, 'category');

  const tagCustomerEnvironmentValue = app?.labels?.filter(
    (label) =>
      label.key === 'CustomerEnvironmentType' &&
      !isNonProdEnvironment(label.value),
  )[0]?.value;

  const sitecorePlanId = app?.labels?.find(
    (label) => label.key === 'SitecorePlanId',
  )?.value;

  const hasConnectTrial =
    sitecorePlanId === 'BASIC_TRIAL' || sitecorePlanId === 'EXTENDED_TRIAL';

  const goLivePlanningEnabled = useFeature('GO_LIVE_PLANNING');
  const { data: goLiveDateData } = useGetGoLiveDateQuery({
    skip: !goLivePlanningEnabled,
  });
  const goLiveDate = goLiveDateData?.serviceNow?.goLiveDate?.goLiveDate;

  const dynamicText = goLiveDate ? getGoLiveDynamicText(goLiveDate) : '';

  return (
    <Drawer
      onClose={onCloseDrawer}
      isOpen={tenantDrawerVisible}
      size={'md'}
      data-testid={'tenantsDrawer'}
      id={'tenants-drawer'}
      autoFocus={false}
    >
      <DrawerOverlay />
      <DrawerContent
        data-behavior-analytics-feature={`tenantDrawer-${product?.displayName}`}
      >
        <HStack pt={7} pb={6}>
          <DrawerCloseButton />
        </HStack>
        <DrawerBody>
          <VStack align={'stretch'} pb={10} gap='8'>
            <VStack align={'start'} gap='5'>
              <Media
                p={0}
                as={'div'}
                size={'2xlarge'}
                _hover={{ backgroundColor: 'transparent', cursor: 'initial' }}
                _focus={{ backgroundColor: 'transparent', cursor: 'initial' }}
              >
                <MediaFigure
                  icon={{ path: tenantsFallbackIcon }}
                  image={{
                    url: product?.icon.src,
                    alt: '',
                  }}
                  color={'transparent'}
                />
                <MediaBody
                  alignSelf={'center'}
                  display={'flex'}
                  flexDirection={'column'}
                >
                  <MediaTitle as={'div'}>
                    <HStack>
                      <Heading fontSize='2xl'>
                        {product?.name ?? app?.name}
                      </Heading>
                      {tagCustomerEnvironmentValue && (
                        <Tag
                          style={{
                            fontSizeAdjust: 'from-font',
                          }}
                          colorScheme='neutral'
                          size='sm'
                          data-testid='tenants-drawer-environment-tag'
                        >
                          {tagCustomerEnvironmentValue.toLowerCase()}
                        </Tag>
                      )}
                      {hasConnectTrial && (
                        <Tag
                          style={{
                            fontSizeAdjust: 'from-font',
                          }}
                          colorScheme='primary'
                          size='sm'
                          data-testid='tenants-drawer-trial-tag'
                        >
                          trial
                        </Tag>
                      )}
                    </HStack>
                  </MediaTitle>
                  <MediaDescription>
                    {product?.displayName ?? app?.displayName ?? ''}
                  </MediaDescription>
                </MediaBody>
              </Media>
              {product?.description && (
                <Text variant='subtle' data-testid='tenantDrawerDescription'>
                  {product?.description}
                </Text>
              )}
            </VStack>
            {goLivePlanningEnabled && goLiveDate && (
              <Alert status='info' borderRadius='md' alignItems='flex-start'>
                <AlertIcon />
                <VStack align='stretch' spacing='1'>
                  <Heading fontSize='sm'>Go-live date</Heading>
                  <AlertDescription fontSize='sm'>
                    {dynamicText}
                  </AlertDescription>
                  <HStack spacing='1'>
                    <Icon viewBox='0 0 24 24' boxSize='4' color='blue.600'>
                      <path fill='currentColor' d={mdiCalendar} />
                    </Icon>
                    <Text fontSize='sm' fontWeight='semibold'>
                      {format(parseISO(goLiveDate), 'MMMM do, yyyy')}
                    </Text>
                  </HStack>
                </VStack>
              </Alert>
            )}
            {product && (
              <Button
                rightIcon={
                  <Icon>
                    <path d={mdiOpenInNew} />
                  </Icon>
                }
                alignSelf='start'
                variant='solid'
                onClick={() => {
                  redirect(product.link.to as string, history);
                }}
                data-behavior-analytics-id={`app-button-open_${product.name}`}
                data-testid={`Open-${product.displayName}`}
                data-url={product.link.to}
              >
                Open app
              </Button>
            )}
            {Object.keys(quickActionsGrouped).map((category, idx) => {
              return (
                <VStack align='stretch' key={idx}>
                  {category.toLowerCase() !== 'quick actions' && (
                    <>
                      <Heading variant='section'>{category}</Heading>
                    </>
                  )}
                  <VStack align='stretch' gap='1'>
                    {quickActionsGrouped[category].map((quickAction: any) => {
                      return (
                        <Media
                          key={quickAction.title}
                          size='large'
                          variant={
                            validateURL(quickAction.link) ? 'link' : undefined
                          }
                          onClick={() =>
                            onLinkClick(quickAction.link as string, history)
                          }
                          data-testid={`app drawer - ${quickAction.title}`}
                          data-behavior-analytics-id={`${product?.name}_${quickAction.title}_${product?.displayName}`}
                          data-url={quickAction['data-url']}
                          alignItems='center'
                        >
                          <MediaFigure
                            icon={{
                              path: quickAction?.iconPath || '',
                            }}
                            image={{
                              url: quickAction.svgIcon,
                              alt: quickAction.title,
                            }}
                            color={quickAction.iconColor}
                          />
                          <MediaBody
                            alignSelf={'center'}
                            display={'flex'}
                            flexDirection={'column'}
                          >
                            <MediaTitle>{quickAction.title}</MediaTitle>
                          </MediaBody>
                          <MediaHover
                            icon={{
                              path: mdiOpenInNew,
                            }}
                          />
                        </Media>
                      );
                    })}
                  </VStack>
                </VStack>
              );
            })}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
